// One route, selected on the Map: its details, rename/recolour/delete, the original GPX, and "along
// this route" (spec §9): the places within the chosen corridor, in order, with their distances. The
// list comes from the server, which measures on the full-resolution geometry.
import { useEffect, useMemo, useRef, useState } from 'react';
import { cardsMap, type Route } from '../../shared/doc.js';
import { CORRIDOR_KM, formatDistance } from '../../shared/geo.js';
import { listPlaces } from '../../shared/map.js';
import { deleteRoute, updateRoute } from '../../shared/ops.js';
import { api, assetUrl, type AlongResult } from '../api.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';

const SOURCE: Record<Route['source'], string> = { gpx: 'imported from GPX', drawn: 'drawn', routed: 'road-following' };

export function RoutePanel({ conn, projectId, route, readOnly, onOpenCard, onClose }: {
  conn: ProjectConn;
  projectId: string;
  route: Route;
  readOnly: boolean;
  onOpenCard: (cardId: string) => void;
  onClose: () => void;
}) {
  const cardsVersion = useYVersion(cardsMap(conn.doc)); // a place added, moved or renamed changes the list
  // D2 (controller ruling): along-route is refetched only when the set of places actually changes
  // (their ids, positions or names) or the route/corridor changes - not on every keystroke anyone
  // makes in an unrelated note or comment, which is what `cardsVersion` alone would trigger on
  // (cardsMap.observeDeep fires on any nested change under any card).
  const placesSignature = useMemo(
    () => listPlaces(conn.doc).map((p) => `${p.id}:${p.lat}:${p.lon}:${p.name}`).join('|'),
    [conn.doc, cardsVersion],
  );
  const [corridorKm, setCorridorKm] = useState<number>(CORRIDOR_KM.default);
  const [along, setAlong] = useState<AlongResult | null>(null);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // A manual Retry (the button below) or a reconnect (conn.status going back to 'connected')
  // both bump this to re-run the fetch effect without waiting for corridorKm/placesSignature to change.
  const [retryNonce, setRetryNonce] = useState(0);
  const wasConnected = useRef(conn.status === 'connected');
  useEffect(() => {
    const connected = conn.status === 'connected';
    if (connected && !wasConnected.current) setRetryNonce((n) => n + 1);
    wasConnected.current = connected;
  }, [conn.status]);

  // Debounced: dragging the slider or typing a place's name sends one request, not dozens.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      api<AlongResult>('GET', `/api/projects/${projectId}/routes/${route.id}/along?corridorKm=${corridorKm}`).then(
        (r) => {
          if (cancelled) return;
          setAlong(r);
          setError('');
        },
        (e: Error) => !cancelled && setError(e.message),
      );
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectId, route.id, route.geometryHash, corridorKm, placesSignature, retryNonce]);

  // A native `change` listener, not React's onChange (which fires on every `input` event — in
  // Chrome, on every incremental colour while the native picker is being dragged): the doc should
  // get one write when the person settles on a colour, not dozens of intermediate ones. Re-attached
  // whenever route.colour changes because the input is keyed on it (so an external recolour shows
  // up without fighting the picker while it's open) — that key swaps in a fresh DOM node each time.
  const colourRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = colourRef.current;
    if (!el) return;
    const onChange = (e: Event) => updateRoute(conn.doc, conn.me, route.id, { colour: (e.target as HTMLInputElement).value });
    el.addEventListener('change', onChange);
    return () => el.removeEventListener('change', onChange);
  }, [conn.doc, conn.me, route.id, route.colour]);

  const name = route.name || 'Untitled route';
  return (
    <section className="route-panel" aria-label={`Route ${name}`}>
      <header>
        <h2>{name}</h2>
        <button className="link" aria-label="Close route" onClick={onClose}>×</button>
      </header>
      <p className="muted small">
        {formatDistance(route.lengthM)} · {SOURCE[route.source]}
        {route.source === 'drawn' && route.waypoints ? `, ${route.waypoints.length} points` : ''}
      </p>
      {!readOnly && (
        <div className="route-edit">
          <label>
            Name
            <input key={route.name} defaultValue={route.name} onBlur={(e) => e.target.value.trim() !== route.name && updateRoute(conn.doc, conn.me, route.id, { name: e.target.value.trim() })} />
          </label>
          <label>
            Colour
            <input key={route.colour} ref={colourRef} type="color" defaultValue={route.colour} />
          </label>
        </div>
      )}
      {route.source === 'gpx' && route.assetId && (
        <a href={assetUrl(route.assetId)} download>
          Download the original GPX
        </a>
      )}
      <h3>Along this route</h3>
      <label className="corridor">
        <span>
          Places within <output>{corridorKm} km</output>
        </span>
        <input type="range" min={CORRIDOR_KM.min} max={CORRIDOR_KM.max} step={0.5} value={corridorKm} aria-label="Corridor width in km" onChange={(e) => setCorridorKm(Number(e.target.value))} />
      </label>
      {error && (
        <p className="error" role="alert">
          {error} <button className="link" onClick={() => setRetryNonce((n) => n + 1)}>Retry</button>
        </p>
      )}
      {along?.approximate && <p className="muted small">Measured on the simplified line: the full-resolution track isn't available.</p>}
      {along &&
        (along.places.length ? (
          <ol className="along-list">
            {along.places.map((p) => (
              <li key={p.placeCardId}>
                <button className="link" onClick={() => onOpenCard(p.placeCardId)}>{p.name}</button>{' '}
                <span className="muted">
                  {formatDistance(p.alongM)} from start · {formatDistance(p.offRouteM)} off route
                </span>
              </li>
            ))}
          </ol>
        ) : (
          // The corridor of the result actually shown, not the slider's (possibly newer, not yet fetched) value.
          <p className="muted small">No places within {along.corridorKm} km of this route.</p>
        ))}
      {!readOnly &&
        (confirmDelete ? (
          <div className="danger-row">
            <span>Delete this route?</span>
            <button
              className="danger"
              onClick={() => {
                conn.undo.stopCapturing(); // its own undo step, never merged with the edit before it
                deleteRoute(conn.doc, route.id);
                onClose();
              }}
            >
              Delete route
            </button>
            <button className="link" onClick={() => setConfirmDelete(false)}>Keep</button>
          </div>
        ) : (
          <button className="link danger" onClick={() => setConfirmDelete(true)}>Delete route…</button>
        ))}
    </section>
  );
}
